import { createApiCall } from './index'

export type MarketItemStatus = 'unreviewed' | 'kept' | 'rejected'

/** Enough to render `CompanyAvatar` without a second call (VRG-537). */
export interface IMarketItemCompanyRef {
  /**
   * VRG-573 — the LINK row's id, not the company's. Confirm and dismiss act on the link,
   * because an unmatched mention has no company id to address it by.
   */
  linkId: string
  /** Null for an unmatched mention: a brand the article named that has no record here. */
  companyId: string | null
  /** The company's name when resolved, otherwise the name as the article wrote it. */
  name: string
  logo: string | null
  /** `suggested` = extraction proposed it. `confirmed` = a human agreed, or assigned it. */
  state: 'suggested' | 'confirmed'
  confidence: 'exact' | 'normalised' | null
}

/** VRG-573 — a pull-quote proposed from an article. */
export interface IMarketItemQuote {
  id: string
  text: string
  speaker: string | null
  approvedAt: string | null
}

export interface IMarketItem {
  id: string
  sourceId: string | null
  sourceName: string | null
  url: string
  title: string | null
  author: string | null
  publication: string | null
  bodyText: string | null
  publishedAt: string | null
  retrievedAt: string
  status: MarketItemStatus
  /** VRG-741 flood guard — backlog, reference only: never in the review queue or the idea feed. */
  backfill?: boolean
  gtmSignal: boolean
  // VRG-537 — outreach.
  problemStatement: string | null
  solutionStatement: string | null
  /** VRG-573 — a `MetricSolution` slug, or null. */
  solution: string | null
  /** VRG-573 — product types from the shared `ALL_PRODUCT_TYPES` list. */
  categories: string[]
  /** VRG-538 — buyer personas, from the company half of `RoleName`. */
  targetRoles: string[]
  /**
   * Confirmed assignments AND model suggestions together, told apart by `state`.
   * Anything that ACTS on a company link must narrow to `confirmed` first.
   */
  companies: IMarketItemCompanyRef[]
  quotes: IMarketItemQuote[]
  /** How many statistics extraction proposed from this item, and how many were approved. */
  statsExtracted: number
  statsApproved: number
  createdAt: string
  updatedAt: string
}

export interface MarketItemListResponse {
  total: number
  data: IMarketItem[]
}

export interface SetMarketItemReviewPayload {
  status?: MarketItemStatus
  gtmSignal?: boolean
  problemStatement?: string | null
  solutionStatement?: string | null
}

/**
 * Market-data corpus API client (VRG-461).
 *
 * Mirrors `announcementsApi` deliberately, including the error class and the `ok()`
 * guard — see that file for why the guard is not optional: `createApiCall`'s
 * errorHandler resolves with the error response rather than throwing on non-401
 * failures, so a 403 that goes unchecked resolves as data and the page renders an
 * empty corpus instead of "you don't have access".
 */
export class MarketItemsApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message)
    this.name = 'MarketItemsApiError'
  }
}

// Named `isMarketItemsForbidden`, not `isForbidden` — `supportApi.ts` and
// `announcementsApi.ts` both already export an `isForbidden` (a pre-existing
// ambiguity in the `services` barrel's `export *` chain, tracked separately as
// VRG-498 alongside `QueryErrorState`'s matching class-coupling issue). A third
// same-named export would add a second TS2308 ambiguity error rather than fixing
// the first one.
export const isMarketItemsForbidden = (error: unknown): boolean =>
  error instanceof MarketItemsApiError && error.status === 403

const ok = (response: any, fallback: string) => {
  if (response.status !== 200 && response.status !== 201) {
    throw new MarketItemsApiError(response?.data?.message || fallback, response?.status ?? 0)
  }
  return response.data
}

export interface ListMarketItemsParams {
  limit?: number
  offset?: number
  sort?: string
  q?: string
  status?: MarketItemStatus
  /** VRG-537 AC4 — "what have we found for this brand" as one call. */
  companyId?: string
}

export const marketItemsApi = {
  async list(params: ListMarketItemsParams): Promise<MarketItemListResponse> {
    const response = await createApiCall({
      method: 'GET',
      url: '/internal/market-items',
      params,
    })
    return ok(response, 'Failed to load the market-data corpus')
  },

  async getById(id: string): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'GET',
      url: `/internal/market-items/${id}`,
    })
    return ok(response, 'Failed to load that content')
  },

  async createFromUrl(url: string): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'POST',
      url: '/internal/market-items',
      data: { url },
    })
    return ok(response, 'Could not add that URL')
  },

  async setReview(id: string, payload: SetMarketItemReviewPayload): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'PATCH',
      url: `/internal/market-items/${id}`,
      data: payload,
    })
    return ok(response, "Couldn't update that item")
  },

  /** Replaces the FULL company set — see MarketItemsInternalController.setCompanies. */
  async setCompanies(id: string, companyIds: string[]): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'PUT',
      url: `/internal/market-items/${id}/companies`,
      data: { companyIds },
    })
    return ok(response, "Couldn't update the assigned companies")
  },

  /** VRG-573 — accept a company link extraction proposed. */
  async confirmCompany(id: string, linkId: string): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/market-items/${id}/companies/${linkId}/confirm`,
    })
    return ok(response, "Couldn't confirm that company")
  },

  /** VRG-573 — remove a company link, suggested or confirmed. Re-proposed on the next run. */
  async dismissCompany(id: string, linkId: string): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'DELETE',
      url: `/internal/market-items/${id}/companies/${linkId}`,
    })
    return ok(response, "Couldn't remove that company")
  },

  /** VRG-573 — approve a pull-quote, meaning it may be sent to a brand verbatim. */
  async approveQuote(id: string, quoteId: string): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'POST',
      url: `/internal/market-items/${id}/quotes/${quoteId}/approve`,
    })
    return ok(response, "Couldn't approve that quote")
  },

  /** VRG-573 — discard a pull-quote. */
  async dismissQuote(id: string, quoteId: string): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'DELETE',
      url: `/internal/market-items/${id}/quotes/${quoteId}`,
    })
    return ok(response, "Couldn't remove that quote")
  },

  /** VRG-538 AC1 — set the article-level targeting tags. Absent key = leave alone. */
  async setTags(
    id: string,
    payload: { solution?: string | null; categories?: string[] | null; targetRoles?: string[] | null },
  ): Promise<IMarketItem> {
    const response = await createApiCall({
      method: 'PATCH',
      url: `/internal/market-items/${id}/tags`,
      data: payload,
    })
    return ok(response, "Couldn't update the tags")
  },
}
